/**
 * React Native Reanimated stub for web
 * Provides a mock implementation for the reanimated library
 */
import React from 'react';

// Mock the Platform object that ConfigHelper looks for
const Platform = {
  select: (obj: any) => obj.default || obj.web || obj.native || null,
  OS: 'web',
};

// Mock animated value
class MockAnimatedValue {
  constructor(public value: any = 0) {}
  setValue(val: any) {
    this.value = val;
  }
}

// Simple wrapper for animated components on web
const AnimatedView = React.forwardRef((props: any, ref: any) => {
  const { style, children, entering, exiting, ...rest } = props;
  // Merge style arrays into a single object for web
  const mergedStyle = Array.isArray(style) ? Object.assign({}, ...style) : style;
  // On web, ignore entering/exiting animations - they happen instantly
  return React.createElement('div', { ref, style: mergedStyle, ...rest }, children);
});
AnimatedView.displayName = 'AnimatedView';

const AnimatedScrollView = React.forwardRef((props: any, ref: any) => {
  const { style, children, entering, exiting, ...rest } = props;
  const mergedStyle = Array.isArray(style) ? Object.assign({}, ...style) : style;
  // On web, ignore entering/exiting animations - they happen instantly
  return React.createElement('div', { 
    ref, 
    style: { ...mergedStyle, overflowY: 'auto' }, 
    ...rest 
  }, children);
});
AnimatedScrollView.displayName = 'AnimatedScrollView';

const AnimatedText = React.forwardRef((props: any, ref: any) => {
  const { style, children, entering, exiting, ...rest } = props;
  const mergedStyle = Array.isArray(style) ? Object.assign({}, ...style) : style;
  // On web, ignore entering/exiting animations - they happen instantly
  return React.createElement('span', { ref, style: mergedStyle, ...rest }, children);
});
AnimatedText.displayName = 'AnimatedText';

const AnimatedImage = React.forwardRef((props: any, ref: any) => {
  const { style, source, entering, exiting, ...rest } = props;
  const mergedStyle = Array.isArray(style) ? Object.assign({}, ...style) : style;
  const src = typeof source === 'object' ? source.uri : source;
  // On web, ignore entering/exiting animations - they happen instantly
  return React.createElement('img', { ref, src, style: mergedStyle, ...rest });
});
AnimatedImage.displayName = 'AnimatedImage';

// Mock the createAnimatedComponent function
export const createAnimatedComponent = (Component: any) => {
  const Wrapped = React.forwardRef((props: any, ref: any) => {
    // For class components and function components, use createElement
    return React.createElement(Component, { ...props, ref });
  });
  Wrapped.displayName = `Animated(${Component.displayName || Component.name || 'Component'})`;
  return Wrapped;
};

// Create a stub for entering/exiting animations
const createEnteringAnimation = (name: string) => {
  const createAnimationObject = (config?: any) => {
    const animationObj = {
      delay: (ms: any) => createAnimationObject(config),
      duration: (ms: any) => createAnimationObject(config),
      damping: (val: any) => createAnimationObject(config),
      mass: (val: any) => createAnimationObject(config),
      stiffness: (val: any) => createAnimationObject(config),
      overshootClamping: (val: any) => createAnimationObject(config),
      restSpeedThreshold: (val: any) => createAnimationObject(config),
      restDisplacementThreshold: (val: any) => createAnimationObject(config),
      withInitialValues: (val: any) => createAnimationObject(config),
      randomDelay: () => createAnimationObject(config),
      springify: () => createAnimationObject(config),
      withCallback: (cb: any) => createAnimationObject(config),
    };
    return animationObj;
  };
  return createAnimationObject();
};

// Export entering animations
export const FadeIn = createEnteringAnimation('FadeIn');
export const FadeOut = createEnteringAnimation('FadeOut');
export const FadeInDown = createEnteringAnimation('FadeInDown');
export const FadeInUp = createEnteringAnimation('FadeInUp');
export const FadeOutDown = createEnteringAnimation('FadeOutDown');
export const FadeOutUp = createEnteringAnimation('FadeOutUp');
export const FadeInLeft = createEnteringAnimation('FadeInLeft');
export const FadeInRight = createEnteringAnimation('FadeInRight');
export const FadeOutLeft = createEnteringAnimation('FadeOutLeft');
export const FadeOutRight = createEnteringAnimation('FadeOutRight');
export const SlideIn = createEnteringAnimation('SlideIn');
export const SlideOut = createEnteringAnimation('SlideOut');
export const SlideInDown = createEnteringAnimation('SlideInDown');
export const SlideInUp = createEnteringAnimation('SlideInUp');
export const SlideInLeft = createEnteringAnimation('SlideInLeft');
export const SlideInRight = createEnteringAnimation('SlideInRight');
export const SlideOutDown = createEnteringAnimation('SlideOutDown');
export const SlideOutUp = createEnteringAnimation('SlideOutUp');
export const SlideOutLeft = createEnteringAnimation('SlideOutLeft');
export const SlideOutRight = createEnteringAnimation('SlideOutRight');
export const ZoomIn = createEnteringAnimation('ZoomIn');
export const ZoomOut = createEnteringAnimation('ZoomOut');
export const BounceIn = createEnteringAnimation('BounceIn');
export const BounceOut = createEnteringAnimation('BounceOut');
export const StretchInY = createEnteringAnimation('StretchInY');
export const StretchOutY = createEnteringAnimation('StretchOutY');
export const StretchInX = createEnteringAnimation('StretchInX');
export const StretchOutX = createEnteringAnimation('StretchOutX');
export const Layout = createEnteringAnimation('FadeIn');

// Mock Animated namespace - use properly defined components
export const Animated = {
  View: AnimatedView,
  ScrollView: AnimatedScrollView,
  Text: AnimatedText,
  Image: AnimatedImage,
  createValue: () => new MockAnimatedValue(),
  Value: MockAnimatedValue,
  event: () => () => {},
  timing: () => ({
    start: () => {},
    stop: () => {},
  }),
  parallel: () => ({
    start: () => {},
    stop: () => {},
  }),
  sequence: () => ({
    start: () => {},
    stop: () => {},
  }),
};

// Export interpolate function
export const interpolate = (
  value: any,
  inputRange: any,
  outputRange: any,
  config?: any
) => value;

// Export useAnimatedStyle hook
export const useAnimatedStyle = () => ({});

// Export useAnimatedReaction hook
export const useAnimatedReaction = () => {};

// Export withTiming
export const withTiming = (targetValue: any, config?: any) => targetValue;

// Export withSpring
export const withSpring = (targetValue: any, config?: any) => targetValue;

// Export withRepeat
export const withRepeat = (animation: any, numberOfReps?: number, reverse?: boolean, callback?: any) => animation;

// Export withDelay
export const withDelay = (delayMs: any, animation: any) => animation;

// Export runOnJS
export const runOnJS = (fn: any) => fn;

// Export useSharedValue hook
export const useSharedValue = (initialValue: any) => ({
  value: initialValue,
});

// Export useAnimatedRef hook
export const useAnimatedRef = (initialRef?: any) => {
  const ref = React.useRef(initialRef || null);
  return ref;
};

// Export useAnimatedScrollHandler hook
export const useAnimatedScrollHandler = (handlers: any) => {
  // Return a handler that web ScrollView can use
  return (event: any) => {
    if (handlers.onScroll) {
      handlers.onScroll(event);
    }
  };
};

// Export useScrollViewOffset hook
export const useScrollViewOffset = (aref: any) => {
  // Return a shared value that represents scroll offset
  return React.useRef({
    value: 0,
  }).current;
};

// Export Easing object with all easing functions
export const Easing = {
  linear: () => 0,
  ease: () => 0,
  quad: () => 0,
  cubic: () => 0,
  poly: () => (t: any) => t,
  sin: () => 0,
  circle: () => 0,
  exp: () => 0,
  elastic: () => 0,
  back: () => 0,
  bounce: () => 0,
  bezier: (x1: any, y1: any, x2: any, y2: any) => (t: any) => t,
  in: (easing: any) => easing,
  out: (easing: any) => easing,
  inOut: (easing: any) => easing,
};

// Export ReduceMotion constant
export const ReduceMotion = {
  System: 'system',
  Always: 'always',
  Never: 'never',
};

// Export useDerivedValue hook
export const useDerivedValue = (derivedValue: any) => ({
  value: derivedValue,
});

// Export addWhitelistedUIProps for @gorhom/bottom-sheet compatibility
export const addWhitelistedUIProps = (props: string[]) => {
  // No-op for web
};

// Export addWhitelistedNativeProps for react-native-redash compatibility
export const addWhitelistedNativeProps = (props: string[]) => {
  // No-op for web
};

// Export Extrapolation enum for react-native-redash
export const Extrapolation = {
  EXTEND: 'extend',
  CLAMP: 'clamp',
  IDENTITY: 'identity',
};

// Export Extrapolate (alias for Extrapolation)
export const Extrapolate = Extrapolation;

// Export cancelAnimation
export const cancelAnimation = (sharedValue: any) => {
  // No-op on web
};

// Export defineAnimation
export const defineAnimation = (factory: any) => factory();

// Export withSequence for animation sequences
export const withSequence = (...animations: any[]) => {
  return animations.length > 0 ? animations[0] : undefined;
};

// Export useAnimatedProps hook
export const useAnimatedProps = (updater: any) => {
  return {};
};

// Export interpolateColor
export const interpolateColor = (
  value: any,
  inputRange: any,
  outputRange: any,
  colorSpace?: string
) => {
  // Return first output color as fallback
  return outputRange[0];
};

// Export ScaleIn animation
export const ScaleIn = createEnteringAnimation('ScaleIn');

// Export useWorkletCallback hook
export const useWorkletCallback = (callback: any, dependencies?: any) => callback;

// Export useAnimatedGestureHandler hook
export const useAnimatedGestureHandler = (handlers: any, dependencies?: any) => {
  // Return a no-op handler
  return () => {};
};

// Export runOnUI
export const runOnUI = (fn: any) => fn;

// Export makeMutable
export const makeMutable = (value: any) => ({
  value,
  set: (newValue: any) => {
    // No-op on web
  },
  modify: (callback: any) => {
    // No-op on web
  },
});

// Export scrollTo for animated scroll
export const scrollTo = (ref: any, x: number, y: number, animated?: boolean) => {
  // No-op on web
};

// Make Animated object the default export AND add named exports for all functions
(Animated as any).interpolate = interpolate;
(Animated as any).useAnimatedStyle = useAnimatedStyle;
(Animated as any).useAnimatedReaction = useAnimatedReaction;
(Animated as any).withTiming = withTiming;
(Animated as any).withSpring = withSpring;
(Animated as any).withRepeat = withRepeat;
(Animated as any).withDelay = withDelay;
(Animated as any).withSequence = withSequence;
(Animated as any).runOnJS = runOnJS;
(Animated as any).runOnUI = runOnUI;
(Animated as any).useSharedValue = useSharedValue;
(Animated as any).useAnimatedRef = useAnimatedRef;
(Animated as any).useAnimatedScrollHandler = useAnimatedScrollHandler;
(Animated as any).useScrollViewOffset = useScrollViewOffset;
(Animated as any).useAnimatedProps = useAnimatedProps;
(Animated as any).useWorkletCallback = useWorkletCallback;
(Animated as any).useAnimatedGestureHandler = useAnimatedGestureHandler;
(Animated as any).Easing = Easing;
(Animated as any).ReduceMotion = ReduceMotion;
(Animated as any).useDerivedValue = useDerivedValue;
(Animated as any).addWhitelistedUIProps = addWhitelistedUIProps;
(Animated as any).addWhitelistedNativeProps = addWhitelistedNativeProps;
(Animated as any).Extrapolation = Extrapolation;
(Animated as any).Extrapolate = Extrapolate;
(Animated as any).cancelAnimation = cancelAnimation;
(Animated as any).defineAnimation = defineAnimation;
(Animated as any).interpolateColor = interpolateColor;
(Animated as any).makeMutable = makeMutable;
(Animated as any).scrollTo = scrollTo;
(Animated as any).createAnimatedComponent = createAnimatedComponent;
(Animated as any).FadeIn = FadeIn;
(Animated as any).FadeOut = FadeOut;
(Animated as any).FadeInDown = FadeInDown;
(Animated as any).FadeInUp = FadeInUp;
(Animated as any).FadeOutDown = FadeOutDown;
(Animated as any).FadeOutUp = FadeOutUp;
(Animated as any).FadeInLeft = FadeInLeft;
(Animated as any).FadeInRight = FadeInRight;
(Animated as any).FadeOutLeft = FadeOutLeft;
(Animated as any).FadeOutRight = FadeOutRight;
(Animated as any).SlideIn = SlideIn;
(Animated as any).SlideOut = SlideOut;
(Animated as any).SlideInDown = SlideInDown;
(Animated as any).SlideInUp = SlideInUp;
(Animated as any).SlideInLeft = SlideInLeft;
(Animated as any).SlideInRight = SlideInRight;
(Animated as any).SlideOutDown = SlideOutDown;
(Animated as any).SlideOutUp = SlideOutUp;
(Animated as any).SlideOutLeft = SlideOutLeft;
(Animated as any).SlideOutRight = SlideOutRight;
(Animated as any).ScaleIn = ScaleIn;
(Animated as any).ZoomIn = ZoomIn;
(Animated as any).ZoomOut = ZoomOut;
(Animated as any).BounceIn = BounceIn;
(Animated as any).BounceOut = BounceOut;
(Animated as any).StretchInY = StretchInY;
(Animated as any).StretchOutY = StretchOutY;
(Animated as any).StretchInX = StretchInX;
(Animated as any).StretchOutX = StretchOutX;

export default Animated;
